"use client";

import { useState, useMemo, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import countryList from "react-select-country-list";
import summaryApi from "../common/index"; 
import { toast } from "react-hot-toast";

export default function CheckoutForm() {
  const [cartItems, setCartItems] = useState([]);
  const [loadingCart, setLoadingCart] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);

  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("Pakistan");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [address, setAddress] = useState("");
  const [apartment, setApartment] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [phone, setPhone] = useState("");
  const [saveAddress, setSaveAddress] = useState(false);

  const countries = useMemo(() => countryList().getData(), []);

  const fetchCartItemsFromBackend = async () => {
    setLoadingCart(true);
    try {
      const res = await fetch(summaryApi.addToCartProductView.url, {
        method: summaryApi.addToCartProductView.method,
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        setCartItems(data.data || []);
      } else {
        setCartItems([]);
      }
    } catch (err) {
      console.error("Failed to load cart items:", err);
      setCartItems([]);
    }
    setLoadingCart(false);
  };

  useEffect(() => {
    fetchCartItemsFromBackend();
  }, []);

  const subtotal = (cartItems || []).reduce(
    (sum, item) => sum + (item.productId?.priceRs || 0) * item.quantity,
    0
  );

  const isCartEmpty = cartItems.length === 0;

  const handlePlaceOrder = async () => {
    if (isCartEmpty) return toast.error("Cart is empty!");

    // Validate required fields
    if (!email || !firstName || !lastName || !address || !city || !country || !phone) {
      return toast.error("Please fill all required fields");
    }

    setPlacingOrder(true);

    try {
      // Create Stripe checkout session
      const res = await fetch(summaryApi.createCheckoutSession.url, {
        method: summaryApi.createCheckoutSession.method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          country,
          firstName,
          lastName,
          company,
          address,
          apartment,
          city,
          postalCode,
          phone,
        }),
      });

      const data = await res.json();

      if (data.success && data.data?.url) {
        // Redirect to Stripe Checkout page
        window.location.href = data.data.url;
      } else {
        toast.error(data.message || "Failed to create checkout session");
        setPlacingOrder(false);
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while processing your order");
      setPlacingOrder(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-8">
            {/* Contact */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium">Contact</h2>
              <Input
                type="email"
                placeholder="Email (Required)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isCartEmpty}
                className="w-full"
              />
            </div>

            {/* Delivery */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium">Delivery</h2>

              <div>
                <Label className="text-sm text-gray-600">Country / Region</Label>
                <Select value={country} onValueChange={setCountry} disabled={isCartEmpty}>
                  <SelectTrigger className="w-full mt-1 bg-white border border-gray-300 text-gray-900 hover:bg-gray-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto bg-white border border-gray-300 shadow-lg">
                    {countries.map((c) => (
                      <SelectItem key={c.value} value={c.label} className="text-gray-900 hover:bg-gray-100">
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  placeholder="First name (Required)"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  disabled={isCartEmpty}
                />
                <Input
                  placeholder="Last name (Required)"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  disabled={isCartEmpty}
                />
              </div>

              <Input
                placeholder="Company (optional)"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                disabled={isCartEmpty}
              />

              <Input
                placeholder="Address (Required)"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
                disabled={isCartEmpty}
              />

              <Input
                placeholder="Apartment, suite / P.O. box etc. (optional)"
                value={apartment}
                onChange={(e) => setApartment(e.target.value)}
                disabled={isCartEmpty}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  placeholder="City (Required)"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  required
                  disabled={isCartEmpty}
                />
                <Input
                  placeholder="Postal code (optional)"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  disabled={isCartEmpty}
                />
              </div>

              <Input
                placeholder="Phone (Required)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                disabled={isCartEmpty}
              />

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="billing-address"
                  checked={saveAddress}
                  onCheckedChange={setSaveAddress}
                  disabled={isCartEmpty}
                  className="w-5 h-5 border border-gray-300 rounded checked:bg-gray-900 checked:border-gray-900"
                />
                <Label htmlFor="billing-address" className="text-sm">
                  Use delivery address as billing address
                </Label>
              </div>

              <Button
                className="w-full bg-gray-900 hover:bg-black text-white py-3 font-semibold"
                onClick={handlePlaceOrder}
                disabled={placingOrder || isCartEmpty}
              >
                {placingOrder ? "Placing Order..." : "Place Order"}
              </Button>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:sticky lg:top-8 lg:h-fit">
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
              {loadingCart ? (
                <p className="text-center text-gray-500 py-10">Loading cart...</p>
              ) : isCartEmpty ? (
                <div className="flex flex-col items-center justify-center py-10 space-y-4">
                  <p className="text-gray-600 text-center text-lg">
                    Your cart is empty! <br />
                    Start adding products to your cart.
                  </p>
                  <a
                    href="/products"
                    className="mt-2 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                  >
                    Browse Products
                  </a>
                    {/* Go to Home button */}
        <a
          href="/"
          className="mt-2 px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-900"
        >
          Go to Home
        </a>
                </div>
              ) : (
                <>
                  {cartItems.map((item) => {
                    const product = item.productId;
                    if (!product) return null;
                    return (
                      <div key={item._id} className="flex items-start gap-4">
                        <div className="relative">
                          <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                            <img
                              src={product.productImages?.[0]?.url}
                              className="w-12 h-12 rounded"
                              alt={product.productName}
                            />
                          </div>
                          <div className="absolute -top-2 -right-2 w-5 h-5 bg-gray-500 text-white text-xs rounded-full flex items-center justify-center">
                            {item.quantity}
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-sm">{product.productName}</h3>
<p className="text-gray-500 text-xs">Brand: {item.productId?.brandName || "N/A"}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">
                            £ {(product.priceRs * item.quantity).toLocaleString("en-IN")}
                          </p>
                        </div>
                      </div>
                    );
                  })}

                  {/* Order Summary */}
                  <div className="space-y-3 pt-4 border-t border-gray-200">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>£ {subtotal.toLocaleString("en-IN")}</span>
                    </div>
                    <div className="flex justify-between text-lg font-medium pt-3 border-t border-gray-200">
                      <span>Total</span>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">incl.</div>
                        <div>£ {subtotal.toLocaleString("en-IN")}</div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
